export interface Pricing {
    title: string;
    creditNumber?: number;
    head: string;
    headRoute?: PaymentTypesRoute;
    offreId?: number;
    price: number;
    description?: string;
    text?: string;
    featuresRange?: number[];
    features?: string[];
    priceYear?: number;
    isRecurringPayment: boolean;
    link?: string;
    /** true = offre retirée de la vente : masquée des grilles, mais conservée
     *  pour afficher l'abonnement des clients qui l'ont déjà souscrite. */
    freeze?: boolean;
    /** true = offre vendue UNIQUEMENT à l'année (offres 2026, plus de mensuel).
     *  Neutralise la bascule mensuel/annuel pour ce plan. */
    annualOnly?: boolean;

    // --- Présentation des offres 2026. Champs FACULTATIFS et distincts de
    // `features`/`featuresRange`, que d'autres écrans utilisent déjà : les
    // renseigner n'affecte que les cartes qui les lisent.
    /** Pastille de contexte : « L'identité compromise », « La prévention »… */
    badge?: string;
    /** Situation du client, en une phrase, telle qu'elle s'affiche sous le titre. */
    accroche?: string;
    /** Unité vendue, affichée à côté du prix : « l'intervention », « an »… */
    uniteTarif?: string;
    /** Ce que le prix recouvre dans le temps (reconduction, échéances). */
    mentionRecurrence?: string;
    /** Ce que l'offre apporte, une ligne par bénéfice. */
    avantages?: string[];
    /** Ce que l'offre apporte, en détail : un intitulé et son explication.
     *  Affiché en grille dans la fiche « en savoir plus ». Prime sur `avantages`,
     *  qui reste la forme courte pour les listes. */
    modules?: {
        titre: string;
        texte?: string;
        /** Mention posée à côté de l'intitulé : « Gratuit », « Nouveau »… */
        marqueur?: string;
    }[];
    /** Précision sous le prix : « Soit 6,58 € par mois, prélevés une fois par an. » */
    sousPrix?: string;
    /** Libellé du bouton d'action. */
    cta?: string;
    /** Ce que le prix comprend obligatoirement en plus de la prestation elle-même.
     *  Pour les offres 2026 : l'abonnement Premium Hub, dont la 1re année est
     *  incluse dans le prix affiché (199 = 120 + 79). Ce n'est PAS une option :
     *  le client ne peut pas la refuser, elle est déjà dans ce qu'il paie. */
    inclus?: {
        title: string;
        text: string;
    };
    /** Mise en avant visuelle (« Le plus demandé »). */
    miseEnAvant?: string;
}


export enum DefaultOffres {
    GRATUIT = "Gratuit", // id = 1
    SERENITE_PREMIUM = "Sérénité premium", // id = 2
    FAMILLE_PREMIUM = "Famille premium", // id = 3
    SERENITE = "Sérénité", // id = 4
    FAMILLE = "Famille", // // id = 5
    // --- Offres 2026. Ces libellés sont envoyés au backend (subscriptionType)
    // et doivent correspondre EXACTEMENT à DefaultOffres côté serveur
    // (server/src/utils/enums.ts) : sinon getPriceId renvoie un price ID vide.
    PREMIUM_HUB = "ID Protect Premium Hub", // id = 10
    PREMIUM_HUB_FAMILLE = "ID Protect Premium Hub Famille", // id = 11
}

export enum PaymentTypes {
    CREDIT = "Credit de Verification",
    ID_TRACKER = "ID Tracker",
    ACCOMAPGNEMENT = "Accompagnement",
    ID_PAY = "ID Pay",
}

/**
 * Offres d'accompagnement de gamme SUPÉRIEURE : Standard (7) et Premium (8),
 * remplacées en 2026 par ID Alerte Active (13) et Cellule de crise SOS (14).
 * Tout le reste relève de la gamme d'entrée, dont Verrou Crédit (12) qui succède
 * à Accompagnement Basic (6) — décision Q du 2026-08-18.
 *
 * On énumère les offres supérieures et non les basiques, pour conserver le repli
 * des anciens tests `offreId >= 7` sur un identifiant inattendu. Même liste côté
 * serveur (`utils/enums.ts`) : les deux doivent rester alignées.
 */
/**
 * Accompagnements 2026 dont le prix affiché INCLUT la première année d'ID Protect
 * Premium Hub : 199 € = 120 € d'accompagnement + 79 € d'abonnement, 299 € = 220 + 79.
 * Le client est donc abonné dès le premier jour — il l'a payé — puis 79 €/an.
 * Le Verrou Crédit (12) reste un produit autonome, sans abonnement.
 */
export const ACCOMPAGNEMENTS_AVEC_ABONNEMENT = [13, 14];

export function inclutAbonnementPremiumHub(offreId: any): boolean {
    return ACCOMPAGNEMENTS_AVEC_ABONNEMENT.includes(Number(offreId));
}

export const ADVANCED_ACCOMPANIMENT_OFFRE_IDS = [7, 8, 13, 14];

export function isBasicAccompanimentOffer(offreId: any): boolean {
    return !ADVANCED_ACCOMPANIMENT_OFFRE_IDS.includes(Number(offreId));
}

export enum PaymentTypesRoute {
    CREDIT = "credits",
    ID_TRACKER = "id-tracker",
    ACCOMAPGNEMENT = "accompaniment",
    ID_PAY = "id-pay",
}

export const FEATURES = [
    "Nous vous protégeons chez les 5 acteurs les plus fraudés",
    "Mise en opposition de vos documents chez ID Protect",
    "Echange téléphonique avec un expert ID Protect",
    "Consultation des fichiers d’usurpation d’identité",
    "Rédaction et envoie des courriers par ID Protect",
    "Etude et accompagnement sur-mesure par nos juristes",
];

export const ASSISTANCE_PRICING: Pricing[] = [
    {
        head: PaymentTypes.ACCOMAPGNEMENT,
        headRoute: PaymentTypesRoute.ACCOMAPGNEMENT,
        title: "BASIC",
        freeze: true, // remplacé par Verrou Crédit — plus à la vente
        price: 79,
        offreId: 6,
        description: "1 dossier/pers",
        featuresRange: [0, 1, 2],
        features: [
            "Nous vous protégeons chez les 5 acteurs les plus fraudés",
            "Mise en opposition de vos documents chez ID Protect",
            "Echange téléphonique avec un expert ID Protect",
        ],
        isRecurringPayment: false,
        link: 'https://idprotect.fr/produit/accompagnement-basic'
    },
    {
        head: PaymentTypes.ACCOMAPGNEMENT,
        headRoute: PaymentTypesRoute.ACCOMAPGNEMENT,
        title: "STANDARD",
        freeze: true, // remplacé par ID Alerte Active — plus à la vente
        price: 129,
        offreId: 7,
        description: "1 dossier/pers",
        featuresRange: [0, 1, 2, 3, 4],
        features: [
            "Nous vous protégeons chez les 5 acteurs les plus fraudés",
            "Mise en opposition de vos documents chez ID Protect",
            "Echange téléphonique avec un expert ID Protect",
            "Consultation des fichiers d’usurpation d’identité",
            "Rédaction et envoie des courriers par ID Protect",
        ],
        isRecurringPayment: false,
        link: 'https://idprotect.fr/produit/accompagnement-standard'
    },
    {
        head: PaymentTypes.ACCOMAPGNEMENT,
        headRoute: PaymentTypesRoute.ACCOMAPGNEMENT,
        title: "PREMIUM",
        freeze: true, // remplacé par Cellule de crise SOS — plus à la vente
        price: 489,
        offreId: 8,
        description: "1 dossier/pers",
        featuresRange: [0, 1, 2, 3, 4, 5],
        features: [
            "Nous vous protégeons chez les 5 acteurs les plus fraudés",
            "Mise en opposition de vos documents chez ID Protect",
            "Echange téléphonique avec un expert ID Protect",
            "Consultation des fichiers d’usurpation d’identité",
            "Rédaction et envoie des courriers par ID Protect",
            "Etude et accompagnement sur-mesure par nos juristes",
        ],
        isRecurringPayment: false,
        link: 'https://idprotect.fr/produit/accompagnement-premium'
    },
    // --- Offres 2026, ajoutées EN FIN DE TABLEAU. Plusieurs écrans indexent
    // cette grille par POSITION (ASSISTANCE_PRICING[1]…) : ne jamais insérer
    // au milieu ni réordonner.
    {
        head: PaymentTypes.ACCOMAPGNEMENT,
        headRoute: PaymentTypesRoute.ACCOMAPGNEMENT,
        title: "Verrou Crédit",
        price: 70,
        offreId: 12,
        description: "1 dossier/pers",
        featuresRange: [0, 1, 2],
        features: [
            "Nous vous protégeons chez les 5 acteurs les plus fraudés",
            "Mise en opposition de vos documents chez ID Protect",
            "Echange téléphonique avec un expert ID Protect",
        ],
        isRecurringPayment: false,
        badge: "Produit autonome · sans abonnement",
        accroche: "Un fraudeur qui détient vos papiers peut ouvrir un crédit à la consommation en votre nom, en quelques minutes.",
        uniteTarif: "paiement unique",
        avantages: [
            "Alertes d'interdiction de prêt auprès des grands organismes de crédit",
            "Sofinco, Cofidis, Cetelem et les autres réseaux de crédit conso",
            "Verrouillage avant que le fraudeur n'ouvre la porte",
        ],
        modules: [
            {
                titre: "Alertes d’interdiction de prêt",
                texte: "Émises auprès des grands organismes de crédit à la consommation.",
            },
            {
                titre: "Sofinco, Cofidis, Cetelem",
                texte: "Et les autres réseaux de crédit à la consommation couverts par le dispositif.",
            },
            {
                titre: "Verrouillage préventif",
                texte: "La porte est fermée avant que le fraudeur ne tente de l’ouvrir.",
            },
            {
                titre: "Paiement unique",
                marqueur: "Sans abonnement",
                texte: "Aucune reconduction, aucun prélèvement les années suivantes.",
            },
        ],
        cta: "Souscrire au Verrou Crédit",
    },
    {
        head: PaymentTypes.ACCOMAPGNEMENT,
        headRoute: PaymentTypesRoute.ACCOMAPGNEMENT,
        title: "ID Alerte Active",
        price: 199,
        offreId: 13,
        description: "1 dossier/pers",
        featuresRange: [0, 1, 2, 3, 4],
        features: [
            "Nous vous protégeons chez les 5 acteurs les plus fraudés",
            "Mise en opposition de vos documents chez ID Protect",
            "Echange téléphonique avec un expert ID Protect",
            "Consultation des fichiers d’usurpation d’identité",
            "Rédaction et envoie des courriers par ID Protect",
        ],
        isRecurringPayment: false,
        miseEnAvant: "Le plus demandé",
        badge: "L’identité compromise",
        accroche: "Vos papiers sont partis dans une arnaque : fausse location, phishing… mais rien n’a encore été utilisé en votre nom.",
        uniteTarif: "l’intervention",
        mentionRecurrence: "Paiement unique, puis 79 €/an (Premium Hub) dès la 2e année.",
        avantages: [
            "Tout le Premium Hub, sans exception",
            "Mise en opposition de vos documents auprès des organismes les plus ciblés",
            "5 courriers d’opposition rédigés, certifiés et envoyés en votre nom",
            "Vérification qu’aucun compte bancaire n’a été ouvert à votre nom à votre insu",
            "Consultation des fichiers FICP et FCC (Banque de France)",
            "Signalement de vos documents compromis auprès des 5 principaux groupes bancaires",
        ],
        modules: [
            {
                titre: "Tout le Premium Hub",
                marqueur: "Inclus",
                texte: "Sans exception. La première année d’abonnement est comprise dans le prix.",
            },
            {
                titre: "Mise en opposition",
                texte: "Vos documents sont signalés auprès des organismes les plus ciblés par la fraude.",
            },
            {
                titre: "5 courriers d’opposition",
                texte: "Rédigés, certifiés et envoyés en votre nom, sans démarche de votre part.",
            },
            {
                titre: "Contrôle bancaire",
                texte: "Vérification qu’aucun compte n’a été ouvert à votre nom à votre insu.",
            },
            {
                titre: "Fichiers FICP et FCC",
                texte: "Consultation des fichiers d’incidents de la Banque de France.",
            },
            {
                titre: "Alerte aux grands groupes bancaires",
                texte: "Vos documents compromis sont signalés aux 5 principaux réseaux.",
            },
        ],
        cta: "Bloquer la menace",
        inclus: {
            title: "Premium Hub inclus",
            text: "La première année d’abonnement est comprise dans le prix : vous en avez tous les bénéfices dès aujourd’hui, puis 79 €/an à partir de la 2e année.",
        },
    },
    {
        head: PaymentTypes.ACCOMAPGNEMENT,
        headRoute: PaymentTypesRoute.ACCOMAPGNEMENT,
        title: "Cellule de crise SOS",
        price: 299,
        offreId: 14,
        description: "1 dossier/pers",
        featuresRange: [0, 1, 2, 3, 4, 5],
        features: [
            "Nous vous protégeons chez les 5 acteurs les plus fraudés",
            "Mise en opposition de vos documents chez ID Protect",
            "Echange téléphonique avec un expert ID Protect",
            "Consultation des fichiers d’usurpation d’identité",
            "Rédaction et envoie des courriers par ID Protect",
            "Etude et accompagnement sur-mesure par nos juristes",
        ],
        isRecurringPayment: false,
        badge: "L’identité compromise",
        accroche: "Usurpation avérée : huissiers, interdit bancaire. Vous avez besoin d’une intervention humaine d’urgence.",
        uniteTarif: "l’intervention",
        mentionRecurrence: "Paiement unique, puis 79 €/an (Premium Hub) dès la 2e année.",
        avantages: [
            "Tout l’ID Alerte Active, sans exception",
            "Un juriste spécialisé affecté à votre dossier",
            "Contestation des dettes frauduleuses jusqu’à gain de cause",
            "Démarches menées à votre place, relances comprises",
            "Un interlocuteur unique jusqu’à la résolution",
        ],
        modules: [
            {
                titre: "Tout l’ID Alerte Active",
                marqueur: "Inclus",
                texte: "Sans exception, Premium Hub compris.",
            },
            {
                titre: "Un juriste spécialisé",
                texte: "Affecté à votre dossier, du premier jour jusqu’à la résolution.",
            },
            {
                titre: "Contestation des dettes",
                texte: "Les dettes frauduleuses sont contestées jusqu’à gain de cause.",
            },
            {
                titre: "Démarches à votre place",
                texte: "Relances comprises : vous n’avez rien à suivre ni à relancer.",
            },
            {
                titre: "Un interlocuteur unique",
                texte: "La même personne vous suit jusqu’au bout du dossier.",
            },
        ],
        cta: "Être pris en charge",
        inclus: {
            title: "Premium Hub inclus",
            text: "La première année d’abonnement est comprise dans le prix : vous en avez tous les bénéfices dès aujourd’hui, puis 79 €/an à partir de la 2e année.",
        },
    },
];

export const ID_TRACKER_PRICING: Pricing[] = [
    {
        head: PaymentTypes.ID_TRACKER,
        headRoute: PaymentTypesRoute.ID_TRACKER,
        offreId: 1,
        title: DefaultOffres.GRATUIT,
        price: 0,
        priceYear: 0,
        description: "  ",
        text: "La prévention contre l’usurpation d’identité",
        features: [
            "ID Block : Faites opposition à vos documents perdus, volés et usurpés",
            "ID Filigrane : Protège vos documents contre les utilisations frauduleuses",
            "ID Tracker : vous alerte si votre identité est utilisée chez nos partenaires",
            "ID Locker : partage vos données avec des utilisateurs vérifiés uniquement",
        ],
        isRecurringPayment: true,
        link: ''
    },
    {
        head: PaymentTypes.ID_TRACKER,
        headRoute: PaymentTypesRoute.ID_TRACKER,
        freeze: true, // remplacée par ID Protect Premium Hub — plus à la vente
        offreId: 4,
        title: DefaultOffres.SERENITE,
        price: 2.9,
        priceYear: 29.9,
        description: "ID Protect surveille vos données sur le Darkweb, pour prévenir tout risques",
        text: "la protection la plus complète du marché.",
        features: [
           "Les avantages de l'offre gratuite +",
            "ID Verify : Vérifiez l’identité de vos interlocuteurs sur internet",
            "ID Darkweb Monitoring : Surveillance Darkweb appliquée à vos documents d’identité",
            "ID Assistant IA : Notre agent IA spécialiste de la détection de fraude",
            "ID Locker : Partagez vos données avec des utilisateurs vérifiés uniquement",
        ],
        isRecurringPayment: true,
        link: 'https://idprotect.fr/produit/offre-serenite'
    },
    {
        head: PaymentTypes.ID_TRACKER,
        headRoute: PaymentTypesRoute.ID_TRACKER,
        freeze: true, // remplacée par ID Protect Premium Hub Famille — plus à la vente
        offreId: 5,
        title: DefaultOffres.FAMILLE,
        price: 4.9,
        priceYear: 50.9,
        description: "ID Protect surveille vos données sur le Darkweb, pour prévenir tout risques",
        text: "Protéger votre identité et  ainsi que l’ensemble de votre famille.",
        features: [
            "Les avantages de l'offre sérénité +",
            "5 comptes au sein d’une même famille",
        ],
        isRecurringPayment: true,
        link: 'https://idprotect.fr/produit/offre-famille'
    },
    {
        head: PaymentTypes.ID_TRACKER,
        freeze: true,
        headRoute: PaymentTypesRoute.ID_TRACKER,
        offreId: 2,
        title: DefaultOffres.SERENITE_PREMIUM,
        price: 7.9,
        description: "/mois",
        text: "",
        features: [
            "Les avantages de l'offre gratuite +",
            "Scan mensuel du Darkweb pour vos documents",
            "Confiez à nos experts vos démarches administratives",
            "Vérifiez l’identité d’un tiers (3 vérifications/mois)",
            "30% de réduction sur nos accompagnements",
        ],
        isRecurringPayment: true,
    },
    {
        head: PaymentTypes.ID_TRACKER,
        freeze: true,
        headRoute: PaymentTypesRoute.ID_TRACKER,
        offreId: 3,
        title: DefaultOffres.FAMILLE_PREMIUM,
        price: 9.9,
        description: "/mois",
        text: "",
        features: [
            "Les avantages de l'offre gratuite +",
            "Créez gratuitement 4 comptes Premium pour vos proches",
            "Confiez à nos experts vos démarches administratives",
            "Vérifiez l’identité d’un tiers (3 vérifications/mois/compte)",
            "30% de réduction sur nos accompagnements",
        ],
        isRecurringPayment: true,
    },
    // --- Offres 2026, ajoutées EN FIN DE TABLEAU. Plusieurs écrans indexent
    // cette grille par POSITION (ID_TRACKER_PRICING[1], [3]…) : ne jamais
    // insérer au milieu ni réordonner.
    // `annualOnly` : plus d'abonnement mensuel, un seul tarif annuel.
    {
        head: PaymentTypes.ID_TRACKER,
        headRoute: PaymentTypesRoute.ID_TRACKER,
        offreId: 10,
        title: DefaultOffres.PREMIUM_HUB,
        price: 79,
        priceYear: 79,
        annualOnly: true,
        description: "ID Protect surveille vos données sur le Darkweb, pour prévenir tout risques",
        text: "la protection la plus complète du marché.",
        features: [
            "Les avantages de l'offre gratuite +",
            "ID Verify : Vérifiez l’identité de vos interlocuteurs sur internet",
            "ID Darkweb Monitoring : Surveillance Darkweb appliquée à vos documents d’identité",
            "ID Assistant IA : Notre agent IA spécialiste de la détection de fraude",
            "ID Locker : Partagez vos données avec des utilisateurs vérifiés uniquement",
        ],
        isRecurringPayment: true,
        badge: "La prévention",
        accroche: "Vous n’avez subi aucun incident — et vous voulez que ça reste le cas.",
        uniteTarif: "an",
        sousPrix: "Soit 6,58 € par mois, prélevés une fois par an.",
        cta: "Activer ma protection",
        modules: [
            {
                titre: "ID Filigrane",
                marqueur: "Gratuit",
                texte: "Chaque copie que vous envoyez porte le nom de son destinataire et sa date.",
            },
            {
                titre: "Kuma",
                marqueur: "Gratuit",
                texte: "L’assistant IA analyse vos situations douteuses et déclenche le bon outil pour vous.",
            },
            {
                titre: "ID Tracker",
                texte: "Soyez alerté lorsque votre identité est utilisée chez nos partenaires et refusez toute utilisation suspecte en un clic.",
            },
            {
                titre: "ID Darkweb Monitor",
                texte: "Vos documents sont surveillés en continu, et vous êtes alerté s’ils réapparaissent.",
            },
            {
                titre: "ID Verify",
                texte: "Vous vérifiez qu’un document est authentique et qu’un interlocuteur existe vraiment.",
            },
            {
                titre: "ID Locker",
                texte: "Vous partagez une pièce avec un accès qui expire, et vous savez qui l’a ouverte.",
            },
            {
                titre: "Score d’hygiène numérique",
                texte: "Une note qui révèle en un coup d’œil à quel point vos données sont protégées.",
            },
        ],
    },
    {
        head: PaymentTypes.ID_TRACKER,
        headRoute: PaymentTypesRoute.ID_TRACKER,
        offreId: 11,
        title: DefaultOffres.PREMIUM_HUB_FAMILLE,
        price: 79,
        priceYear: 79,
        annualOnly: true,
        description: "ID Protect surveille vos données sur le Darkweb, pour prévenir tout risques",
        text: "Protéger votre identité et  ainsi que l’ensemble de votre famille.",
        features: [
            "Les avantages de l'offre ID Protect Premium Hub +",
            "5 comptes au sein d’une même famille",
        ],
        isRecurringPayment: true,
        badge: "La prévention, à plusieurs",
        accroche: "La même protection, étendue à ceux qui comptent pour vous.",
        uniteTarif: "an",
        sousPrix: "Soit 6,58 € par mois, prélevés une fois par an, pour toute la famille.",
        cta: "Protéger ma famille",
        modules: [
            {
                titre: "5 comptes",
                marqueur: "Inclus",
                texte: "Cinq membres d’un même foyer, chacun avec son propre espace et ses documents.",
            },
            {
                titre: "Tout le Premium Hub",
                texte: "Chaque compte dispose de l’intégralité des outils, sans restriction.",
            },
            {
                titre: "ID Filigrane",
                marqueur: "Gratuit",
                texte: "Chaque copie que vous envoyez porte le nom de son destinataire et sa date.",
            },
            {
                titre: "Kuma",
                marqueur: "Gratuit",
                texte: "L’assistant IA analyse vos situations douteuses et déclenche le bon outil pour vous.",
            },
            {
                titre: "ID Tracker",
                texte: "Soyez alerté lorsque votre identité est utilisée chez nos partenaires et refusez toute utilisation suspecte en un clic.",
            },
            {
                titre: "ID Darkweb Monitor",
                texte: "Vos documents sont surveillés en continu, et vous êtes alerté s’ils réapparaissent.",
            },
            {
                titre: "ID Verify",
                texte: "Vous vérifiez qu’un document est authentique et qu’un interlocuteur existe vraiment.",
            },
            {
                titre: "ID Locker",
                texte: "Vous partagez une pièce avec un accès qui expire, et vous savez qui l’a ouverte.",
            },
            {
                titre: "Score d’hygiène numérique",
                texte: "Une note qui révèle en un coup d’œil à quel point vos données sont protégées.",
            },
        ],
    },
];

export const CREDITS_PRICING: Pricing[] = [
    // Grille 2026 (décision Q) : paliers 10/30/50, palier 100 supprimé. TVA 0 %.
    // `title` porte le nombre attribué : extractNumber() lit le 1er groupe de chiffres.
    { head: PaymentTypes.CREDIT, creditNumber: 10, title: "10 vérifications", price: 10, isRecurringPayment: false, headRoute: PaymentTypesRoute.CREDIT, },
    { head: PaymentTypes.CREDIT, creditNumber: 30, title: "30 vérifications", price: 25, isRecurringPayment: false, headRoute: PaymentTypesRoute.CREDIT },
    { head: PaymentTypes.CREDIT, creditNumber: 50, title: "50 vérifications", price: 40, isRecurringPayment: false, headRoute: PaymentTypesRoute.CREDIT },
];

export const ID_PAY_PRICING: Pricing[] = [
    { head: PaymentTypes.ID_PAY, title: "Vérification d'identité", price: 5, isRecurringPayment: false, headRoute: PaymentTypesRoute.ID_PAY },
];
